import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import type { AppNotificationDto } from '@/api/admin'

type Api = {
  getNotifications: (limit?: number) => Promise<{ data: AppNotificationDto[] }>
  getUnreadNotificationCount: () => Promise<{ data: number }>
  markNotificationRead: (id: string) => Promise<unknown>
  markAllNotificationsRead: () => Promise<unknown>
}

// Maps entityType + event to a route for deep-linking
function notificationUrl(n: AppNotificationDto, isAdmin: boolean): string | null {
  if (!n.entityId || !n.entityType) return null
  if (n.entityType === 'Booking') return isAdmin ? `/admin/bookings/${n.entityId}` : '/portal/bookings'
  if (n.entityType === 'Invoice') return isAdmin ? `/admin/invoices/${n.entityId}` : `/portal/invoices/${n.entityId}`
  if (n.entityType === 'Enquiry') return isAdmin ? '/admin/bookings' : null
  return null
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

interface NotificationBellProps {
  api: Api
  isAdmin?: boolean
  /** Class for the bell button to adapt to dark/light nav backgrounds */
  buttonClass?: string
  /** Where the panel opens. 'down' (default) opens below the bell; 'right' opens to the right, aligned at the bottom — use when the bell is in a sidebar footer */
  panelSide?: 'down' | 'right'
}

export function NotificationBell({ api, isAdmin = false, buttonClass, panelSide = 'down' }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // The 'down' panel is rendered in a portal with fixed positioning: the portal nav is an
  // overflow-x-auto strip, which would otherwise clip the panel and show a stray scrollbar.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: () => api.getUnreadNotificationCount().then(r => r.data),
    refetchInterval: 60_000,
  })

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.getNotifications(30).then(r => r.data),
    enabled: open,
    staleTime: 30_000,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] })
    },
  })

  const markAllRead = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] })
    },
  })

  const toggle = () => {
    if (!open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) })
    }
    setOpen(v => !v)
  }

  // Close on outside click, and when the page moves under a fixed panel
  useEffect(() => {
    if (!open) return
    const outside = (e: MouseEvent) => {
      const t = e.target as Node
      if (!buttonRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false)
    }
    const moved = (e: Event) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', outside)
    window.addEventListener('scroll', moved, true)
    window.addEventListener('resize', moved)
    return () => {
      document.removeEventListener('mousedown', outside)
      window.removeEventListener('scroll', moved, true)
      window.removeEventListener('resize', moved)
    }
  }, [open])

  const handleClick = (n: AppNotificationDto) => {
    if (!n.isRead) markRead.mutate(n.id)
    const url = notificationUrl(n, isAdmin)
    if (url) {
      navigate(url)
      setOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggle}
        className={`relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors cursor-pointer ${buttonClass ?? 'text-slate-400 hover:bg-white/8 hover:text-slate-200'}`}
        title="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-0.5">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (() => {
        const panel = (
          <div
            ref={panelRef}
            style={panelSide === 'down' && pos ? { position: 'fixed', top: pos.top, right: pos.right } : undefined}
            className={`w-80 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden ${
              panelSide === 'right' ? 'absolute left-full bottom-0 ml-3' : pos ? '' : 'absolute right-0 top-full mt-2'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900">Notifications</span>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer"
                  disabled={markAllRead.isPending}
                >
                  Mark all read
                </button>
              )}
            </div>
  
            {/* List */}
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {isLoading && (
                <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
              )}
              {!isLoading && notifications.length === 0 && (
                <div className="text-center py-10">
                  <Bell size={24} className="mx-auto text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">No notifications yet</p>
                </div>
              )}
              {notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 cursor-pointer ${
                    n.isRead ? 'opacity-60' : ''
                  }`}
                >
                  {!n.isRead && (
                    <span className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-indigo-500" />
                  )}
                  {n.isRead && <span className="mt-1.5 shrink-0 w-2 h-2" />}
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm leading-snug ${n.isRead ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{n.body}</p>
                    )}
                    <p className="text-[11px] text-gray-300 mt-1">{timeAgo(n.createdAt)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )
        return panelSide === 'down' && pos ? createPortal(panel, document.body) : panel
      })()}
    </div>
  )
}
